/** Client-side image prep — keeps server load low by resizing before upload */

export const MAX_UPLOAD_DIMENSION = 1200;
export const WARN_FILE_SIZE_MB = 5;

const SUPPORTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Resize image in browser if longest side exceeds maxDimension.
 * @returns {{ file: File, wasResized: boolean, originalDimensions: string, newDimensions: string }}
 */
export async function compressImageFile(file, maxDimension = MAX_UPLOAD_DIMENSION) {
  if (!file?.type?.startsWith('image/')) {
    throw new Error('Invalid image file');
  }

  const objectUrl = URL.createObjectURL(file);

  try {
    const img = await loadImage(objectUrl);
    const { width, height } = img;
    const originalDimensions = `${width}×${height}`;

    if (Math.max(width, height) <= maxDimension) {
      return {
        file,
        wasResized: false,
        originalDimensions,
        newDimensions: originalDimensions,
      };
    }

    const scale = maxDimension / Math.max(width, height);
    const newWidth = Math.round(width * scale);
    const newHeight = Math.round(height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = newWidth;
    canvas.height = newHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas not supported');
    }
    ctx.drawImage(img, 0, 0, newWidth, newHeight);

    const outputType = SUPPORTED_TYPES.includes(file.type) ? file.type : 'image/jpeg';
    const blob = await canvasToBlob(canvas, outputType, outputType === 'image/jpeg' ? 0.92 : undefined);
    const baseName = file.name.replace(/\.[^.]+$/, '') || 'photo';
    const ext = outputType === 'image/png' ? 'png' : outputType === 'image/webp' ? 'webp' : 'jpg';
    const optimized = new File([blob], `${baseName}-optimized.${ext}`, { type: outputType });

    return {
      file: optimized,
      wasResized: true,
      originalDimensions,
      newDimensions: `${newWidth}×${newHeight}`,
    };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = src;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Failed to compress image'))),
      type,
      quality
    );
  });
}

export function formatFileSizeMB(bytes) {
  return (bytes / 1024 / 1024).toFixed(2);
}
